'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Facebook, Linkedin, Twitter } from 'lucide-react'
import { trackAnalyticsEvent } from '@/lib/analytics'
import { clinicIdentity } from '@/lib/clinic-identity'
import CrisisGuidance from '@/components/CrisisGuidance'

export default function Footer() {
  // Navigation links data
  const navLinks = [
    { text: 'Our Services', href: '/services' },
    { text: 'About Us', href: '/about' },
    { text: 'Our Team', href: '/team' },
    { text: 'Careers', href: '/careers' },
    { text: 'Blog', href: '/blog' },
  ]

  // Social media icons data
  const socialIcons = [
    {
      icon: Linkedin,
      href: 'https://www.linkedin.com/company/reachpsychiatry',
      ariaLabel: 'LinkedIn',
    },
    {
      icon: Facebook,
      href: 'https://www.facebook.com/reachpsychiatry',
      ariaLabel: 'Facebook',
    },
    {
      icon: Twitter,
      href: 'https://twitter.com/reachpsychiatry',
      ariaLabel: 'Twitter',
    },
  ]

  return (
    <footer className="flex flex-col w-[calc(100%-2rem)] max-w-[1275px] items-start gap-8 md:gap-[50px] pt-10 md:pt-[55px] pb-8 md:pb-[50px] px-5 sm:px-8 md:px-[60px] relative bg-dark rounded-[45px_45px_0px_0px] mx-auto">
      <div className="flex flex-col items-start gap-10 md:gap-[66px] relative self-stretch w-full">
        {/* Top section with logo, navigation and social icons */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-[155px] relative self-stretch w-full">
          {/* Logo */}
          <Link href="/">
            <Image
              priority
              width={147}
              height={34}
              src="/icons/reach_logo_white.svg"
              alt={`${clinicIdentity.name} Logo`}
              className="w-[147px] h-[34px] object-contain"
            />
          </Link>

          {/* Navigation links */}
          <nav className="flex flex-wrap items-start gap-x-8 gap-y-4 md:gap-10 relative">
            {navLinks.map((link) => (
              <Link
                key={link.text}
                href={link.href}
                className="font-['Space_Grotesk-Regular'] text-white text-base md:text-lg underline hover:text-reach-blue"
              >
                {link.text}
              </Link>
            ))}
          </nav>

          {/* Social media icons */}
          <div className="flex items-center gap-4 lg:ml-auto">
            {socialIcons.map((social) => {
              const IconComponent = social.icon
              return (
                <a
                  key={social.ariaLabel}
                  href={social.href}
                  aria-label={social.ariaLabel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-reach-blue"
                >
                  <IconComponent size={24} />
                </a>
              )
            })}
          </div>
        </div>

        {/* Middle section with contact info and book session button */}
        <div className="flex flex-col md:flex-row items-start justify-between gap-8 md:gap-10 relative self-stretch w-full">
          {/* Contact information */}
          <div className="flex flex-col items-start gap-5 md:gap-[27px] relative w-full md:w-auto">
            <Badge className="px-[7px] py-0 bg-reach-blue rounded-[7px] text-black">
              Contact us:
            </Badge>

            <div className="flex flex-col w-full max-w-[392px] items-start gap-4 md:gap-5 relative">
              <p className="text-white text-sm md:text-base break-all sm:break-normal">
                <span>Email: </span>
                <a
                  href={clinicIdentity.contact.publicEmail.href}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="underline"
                  onClick={() =>
                    trackAnalyticsEvent('email_click', {
                      cta_location: 'footer',
                    })
                  }
                >
                  {clinicIdentity.contact.publicEmail.display}
                </a>
              </p>

              <p className="text-white text-sm md:text-base">
                <span>Phone: </span>
                <a
                  href={clinicIdentity.contact.phone.href}
                  className="underline"
                  onClick={() =>
                    trackAnalyticsEvent('phone_click', {
                      cta_location: 'footer',
                    })
                  }
                >
                  {clinicIdentity.contact.phone.display}
                </a>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <a
                  href={clinicIdentity.contact.landline.href}
                  className="underline"
                  onClick={() =>
                    trackAnalyticsEvent('phone_click', {
                      cta_location: 'footer',
                    })
                  }
                >
                  {clinicIdentity.contact.landline.display}
                </a>
              </p>

              <p className="text-white text-sm md:text-base">
                <span>WhatsApp: </span>
                <a
                  href={clinicIdentity.contact.whatsapp.href}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="underline"
                  onClick={() =>
                    trackAnalyticsEvent('whatsapp_click', {
                      cta_location: 'footer',
                    })
                  }
                >
                  Message us on WhatsApp (
                  {clinicIdentity.contact.whatsapp.display})
                </a>
              </p>

              <p className="text-white text-sm md:text-base">
                <span>Address:&nbsp;&nbsp;</span>
                <a
                  href={clinicIdentity.maps.placeUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="underline"
                  onClick={() =>
                    trackAnalyticsEvent('directions_click', {
                      cta_location: 'footer',
                    })
                  }
                >
                  {clinicIdentity.address.formatted}
                </a>
              </p>

              <p className="text-white text-sm md:text-base">
                <span>Hours: </span>
                {clinicIdentity.hours.display} · Sunday:{' '}
                {clinicIdentity.hours.sunday}
              </p>
            </div>
          </div>

          {/* Book a session card */}
          <Card className="w-full sm:w-[316px] bg-[#292a32] rounded-3xl overflow-hidden border-none flex-shrink-0">
            <CardContent className="px-6 sm:px-10 py-8 sm:py-[58px]">
              <Button
                className="w-full px-[35px] py-5 bg-reach-blue rounded-3xl h-auto"
                asChild
              >
                <Link href="/book">
                  <span className="font-['Space_Grotesk-Regular'] text-black text-lg sm:text-xl text-center">
                    Book a Session
                  </span>
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Urgent-help guidance (P2.13, approved wording): deliberately its own
          block AFTER the routine contact/booking area — government resources,
          never part of the clinic contact list, no analytics. */}
      <CrisisGuidance variant="compact" />

      {/* Bottom section with copyright and privacy policy */}
      <div className="flex flex-col items-start gap-6 md:gap-[50px] relative self-stretch w-full">
        <Separator className="w-full h-px bg-white/20" />

        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-10 relative">
          <p className="text-white text-sm md:text-lg">
            © {new Date().getFullYear()} {clinicIdentity.name}. All Rights
            Reserved.
          </p>

          <Link
            href="/privacy"
            className="text-white text-sm md:text-lg underline"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  )
}
